import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import {
  flashComplaintTitle,
  playComplaintRing,
  requestComplaintNotifyPermission,
  showComplaintBrowserNotification,
  unlockComplaintRing,
} from '../utils/complaintRing';

const isRealComplaint = (row) => {
  if (!row?.id || row.delete_status) return false;
  const src = String(row.source || '').trim();
  if (!src) return true;
  return (
    src === 'customer_rsa_app' ||
    src === 'customer_rsa_bargain' ||
    src === 'customer_rsa_on_demand' ||
    src === 'whatsapp_ai' ||
    src === 'whatsapp_rsa' ||
    src === 'website_rsa' ||
    src === 'enterprise_manual' ||
    src === 'enterprise_api' ||
    src === 'voice_ai' ||
    !!row.enterprise_id ||
    src.includes('rsa') ||
    src.includes('whatsapp') ||
    src.includes('enterprise')
  );
};

export default function useNewComplaintBrowserRing(enabled = true) {
  const [permission, setPermission] = useState(() =>
    typeof Notification === 'undefined' ? 'denied' : Notification.permission,
  );
  const [armed, setArmed] = useState(false);
  const [toast, setToast] = useState(null);
  const seenRef = useRef(new Set());

  const arm = useCallback(async () => {
    unlockComplaintRing();
    const next = await requestComplaintNotifyPermission();
    setPermission(next);
    setArmed(true);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const onFirst = () => {
      arm();
    };
    window.addEventListener('pointerdown', onFirst, { once: true });
    window.addEventListener('keydown', onFirst, { once: true });
    return () => {
      window.removeEventListener('pointerdown', onFirst);
      window.removeEventListener('keydown', onFirst);
    };
  }, [enabled, arm]);

  useEffect(() => {
    if (!enabled) return undefined;

    const channel = supabase
      .channel('admin-new-complaint-ring')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'service_requests' },
        (payload) => {
          const row = payload.new || {};
          if (!isRealComplaint(row)) return;
          const id = String(row.id);
          if (seenRef.current.has(id)) return;
          seenRef.current.add(id);

          const name = String(row.customer_name || 'Customer').trim();
          const phone = String(row.contact_number || '').trim();
          const service = String(row.service_type || 'RSA').trim();
          const body = [name, phone, service].filter(Boolean).join(' · ');
          const path = `/service_advisor/complaints/${id}`;

          playComplaintRing();
          flashComplaintTitle('New complaint');
          showComplaintBrowserNotification({
            title: 'New complaint',
            body,
            url: path,
          });
          setToast({ id, name, phone, service, path });
          window.setTimeout(() => {
            setToast((cur) => (cur?.id === id ? null : cur));
          }, 20000);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled]);

  return {
    permission,
    armed,
    arm,
    toast,
    dismissToast: () => setToast(null),
  };
}
